import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import {
  ArrowRight, ArrowUpRight, CheckCircle2, FileText, Sparkles, ShieldCheck,
  Zap, AlertTriangle, TrendingDown, EyeOff, Cloud, Star, Plus, Minus,
  Receipt, Timer, MousePointerClick,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const DASABO_LOGO = "/logo.png";

const TICKER = [
  "AF/0287 · Meta Platforms Ireland · Consegnata allo SDI",
  "AF/0288 · Amazon Web Services EMEA · In elaborazione",
  "AF/0289 · Google Ireland · TD17 generato in 11s",
  "Uno studio di Bologna ha automatizzato 47 PDF di Meta",
  "Media di elaborazione oggi pomeriggio: 12 secondi",
  "AF/0290 · Hetzner Online GmbH · Consegnata",
  "Freelance a Milano ha collegato il suo Fatture in Cloud",
  "AF/0291 · Canva Pty Ltd · Consegnata allo SDI",
];

// Loghi reali via Simple Icons CDN (SVG monocromatico → colorabile con CSS filter)
const TRUST = [
  { name: "Google", slug: "google" },
  { name: "Meta", slug: "meta" },
  { name: "Amazon", slug: "amazon" },
  { name: "Microsoft", slug: "microsoft" },
  { name: "Zoom", slug: "zoom" },
  { name: "Canva", slug: "canva" },
  { name: "Shopify", slug: "shopify" },
  { name: "Notion", slug: "notion" },
  { name: "OpenAI", slug: "openai" },
  { name: "Slack", slug: "slack" },
  { name: "Figma", slug: "figma" },
  { name: "Stripe", slug: "stripe" },
  { name: "Hetzner", slug: "hetzner" },
  { name: "GitHub", slug: "github" },
  { name: "Linear", slug: "linear" },
  { name: "Adobe", slug: "adobe" },
];

const PROBLEMS = [
  {
    icon: AlertTriangle, tone: "coral",
    kicker: "01 / La verità scomoda",
    title: "Ogni PDF ignorato costa.",
    body: "In tempo, in tranquillità, in IVA persa. E potenzialmente in sanzioni fino a 400€ al mese quando l'AdE bussa.",
  },
  {
    icon: TrendingDown, tone: "blue",
    kicker: "02 / L'IVA che evapora",
    title: "Non deduci, paghi doppio.",
    body: "Senza autofattura il costo non entra in dichiarazione. Fine dei giochi: paghi le tasse su spese già sostenute.",
  },
  {
    icon: EyeOff, tone: "ink",
    kicker: "03 / Venerdì sera, ore 23",
    title: "Caccia al tesoro tra email.",
    body: "Google in una cartella, Meta in un'altra, Hetzner chissà dove. Ogni fine mese la stessa liturgia.",
  },
];

const FEATURES = [
  {
    icon: Sparkles,
    title: "Estrazione neurale del PDF",
    body: "Fornitore, VAT, imponibile, valuta, tipo servizio. L'AI legge come uno stagista attentissimo. Solo che ci mette 12 secondi.",
    color: "blue",
  },
  {
    icon: FileText,
    title: "XML validato prima dello SDI",
    body: "Ogni tag controllato contro il tracciato ufficiale 1.2.2. Se qualcosa non torna, te lo diciamo prima di premere invio.",
    color: "emerald",
  },
  {
    icon: Cloud,
    title: "Reverse charge, automatico",
    body: "Fornitore estero al 0%? Applichiamo il 22% italiano. TD17, TD18, TD19 li scegliamo noi dal contesto della fattura.",
    color: "coral",
  },
  {
    icon: ShieldCheck,
    title: "Anche se sei forfettario",
    body: "RF19 preimpostato, reverse charge integrato. Nessuna magia contabile da imparare — la piattaforma sa cosa fare al posto tuo.",
    color: "ink",
  },
];

const STEPS = [
  {
    n: "01",
    title: "Trascina o inoltra",
    body: "Drag & drop di un PDF, oppure inoltri l'email di Google/Meta al tuo indirizzo dedicato.",
    accent: "blue",
  },
  {
    n: "02",
    title: "L'AI compone",
    body: "L'estrattore neurale identifica il fornitore, l'importo e il tipo documento. Precisione notarile.",
    accent: "emerald",
  },
  {
    n: "03",
    title: "SDI in 60 secondi",
    body: "Verifichi, confermi, invii. Il consiglio del tuo commercialista è già inclusa.",
    accent: "coral",
  },
];

const TESTIMONIALS = [
  {
    quote: "Ho recuperato 400€ di IVA che davo per persi. E ci metto tre respiri per fattura.",
    name: "Giovanni E.", role: "Freelance dev, Torino", initial: "G", accent: "blue",
  },
  {
    quote: "Il mio commercialista mi ha chiesto se avessi assunto qualcuno. Gli ho detto: 'no, un'app'.",
    name: "Fabio S.", role: "Titolare PMI, Verona", initial: "F", accent: "coral",
  },
  {
    quote: "Faccio 40 fatture Google Ads al mese. Prima ci mettevo due giorni. Ora il tempo di un caffè.",
    name: "Antonio M.", role: "Agenzia digital, Milano", initial: "A", accent: "emerald",
  },
];

const PLANS = [
  {
    id: "starter",
    name: "Starter",
    price: "€14,90",
    unit: "una tantum",
    desc: "Ideale per freelance con qualche fattura estera al mese.",
    features: ["25 crediti (fatture)", "Estrazione AI", "Reverse-charge 22%", "XML SDI compliant", "Nessuna scadenza"],
    cta: "Voglio provare",
    highlight: false,
  },
  {
    id: "business",
    name: "Business",
    price: "€24,90",
    unit: "una tantum",
    desc: "Dove sta l'80% dei nostri utenti attivi.",
    features: [
      "60 crediti (fatture)", "Assistenza prioritaria", "Integrazione Fatture in Cloud",
      "Invio SDI diretto", "Timeline notifiche real-time",
    ],
    cta: "Voglio Business",
    highlight: true,
    badge: "Più popolare",
  },
  {
    id: "pro",
    name: "Pro",
    price: "€39,90",
    unit: "una tantum",
    desc: "Per studi professionali e piccole agenzie.",
    features: ["150 crediti (fatture)", "Assistenza dedicata", "Multi-azienda", "Rubrica fornitori", "Onboarding guidato"],
    cta: "Voglio Pro",
    highlight: false,
  },
];

const FAQ = [
  {
    q: "Ma davvero devo fare l'autofattura per Google Ads?",
    a: "Sì, tocca a te. Se hai P.IVA italiana e paghi Google Ireland, Meta Ireland o Amazon Lussemburgo devi integrare la fattura con TD17 verso lo SDI. Vale anche se sei forfettario — anzi, per loro è ancora più stringente perché non hanno il gestionale del commercialista che lo fa in automatico.",
  },
  {
    q: "E se sono in forfettario?",
    a: "Sei nel posto giusto. La piattaforma imposta RF19, applica reverse charge al 22% e sceglie il codice TD corretto senza che tu debba sapere niente di contabilità. Ci pensiamo noi a farti sembrare uno che sa cosa sta facendo.",
  },
  {
    q: "Cosa succede dopo che genero l'XML?",
    a: "Tre strade: (1) lo scarichi e lo giri al commercialista, (2) lo carichi sul portale Fatture e Corrispettivi dell'AdE, oppure (3) — la strada consigliata — colleghi Fatture in Cloud e con un tap parte allo SDI. Poi ricevi RC (consegnata), NS (scartata) o MC (mancata consegna) direttamente nella timeline della fattura.",
  },
  {
    q: "L'email dedicata è una vera magia?",
    a: "Quasi. Ogni account ha un indirizzo tipo `user-xyz@invoices...`: gli inoltri qualunque email di Google/Meta/Amazon con la fattura in allegato e in background creiamo la bozza pronta da verificare. Puoi impostare una regola nel tuo Gmail per farlo automaticamente.",
  },
  {
    q: "Quale codice TD scegliamo?",
    a: "TD17 per servizi esteri (Google Ads, hosting, SaaS). TD18 per beni intracomunitari (es. da altri paesi UE). TD19 per beni ex art.17 c.2 DPR 633/72. Il sistema legge la fattura e sceglie da solo — tu confermi in un tap.",
  },
  {
    q: "Chi vede i miei dati fiscali?",
    a: "Solo tu. Cifriamo i token OAuth di Fatture in Cloud con Fernet AES-128, l'autenticazione è JWT + bcrypt, i PDF originali non vengono conservati dopo l'estrazione, i server sono in UE. Conformità GDPR di serie, non come optional.",
  },
];

export default function Landing() {
  useSeo({
    title: "Autofattura: La Soluzione Automatica per le tue Fatture Estere",
    standalone: true,
    description: "Autofattura estera per freelance e commercialisti italiani: l'AI legge il PDF di Google Ads, Meta, Amazon e AWS, calcola il reverse charge 22%, genera XML FatturaPA TD17/TD18 e lo trasmette allo SDI. Integrazione Fatture in Cloud ufficiale. 5 crediti omaggio.",
    path: "/",
  });
  useRevealOnScroll();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className="min-h-screen bg-[#FAF9F6] text-[#0A0A0A] overflow-x-hidden" data-testid="landing-page">
      {/* NAV */}
      <header
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-[#E8E4D9]"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <span className="font-display font-extrabold text-lg tracking-tight flex items-center gap-1.5 text-[#0A0A0A]">
              Autofattura by
              <img src={DASABO_LOGO} alt="Dasabo" className="h-5 w-auto" />
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-[#4A4A4A]">
            <a href="#features" className="hover:text-[#0A0A0A] transition-colors" data-testid="nav-features">Prodotto</a>
            <a href="#steps" className="hover:text-[#0A0A0A] transition-colors" data-testid="nav-steps">Flusso</a>
            <a href="#pricing" className="hover:text-[#0A0A0A] transition-colors" data-testid="nav-pricing">Prezzi</a>
            <Link to="/commercialisti" className="hover:text-[#0A0A0A] transition-colors" data-testid="nav-commercialisti">Commercialisti</Link>
            <Link to="/blog" className="hover:text-[#0A0A0A] transition-colors" data-testid="nav-blog">Blog</Link>
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <button
                onClick={() => navigate("/app")}
                data-testid="go-to-app-btn"
                className="group inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold bg-[#0A0A0A] text-white hover:bg-[#1D4ED8] transition-colors"
              >
                Vai all&apos;app <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </button>
            ) : (
              <>
                <Link
                  to="/login"
                  data-testid="nav-login"
                  className="hidden sm:inline-flex px-4 py-2 rounded-full text-sm font-medium text-[#4A4A4A] hover:text-[#0A0A0A] transition-colors"
                >
                  Accedi
                </Link>
                <Link
                  to="/register"
                  data-testid="nav-signup"
                  className="group inline-flex items-center gap-1.5 px-5 py-2 rounded-full text-sm font-semibold bg-[#0A0A0A] text-white hover:bg-[#1D4ED8] transition-colors shadow-lg shadow-black/10"
                >
                  Prova gratis <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-20 md:pb-24 noise-bg">
        <div className="mesh-orbs" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left: copy */}
            <div className="lg:col-span-7">
              <Link
                to="/register"
                data-testid="hero-badge"
                className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/70 backdrop-blur-md border border-[#E8E4D9] text-[#0A0A0A] text-xs font-semibold mb-8 hover:bg-white transition-colors shadow-sm"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                Nuovo · Integrazione Fatture in Cloud ufficiale
              </Link>

              <h1 className="font-display font-extrabold text-[3.5rem] sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[0.92] tracking-tighter text-[#0A0A0A]">
                Le fatture di{" "}
                <span className="font-serif-accent text-[#F97316]">Google Ads</span>
                <br />
                <span className="relative inline-block">
                  <span className="relative z-10">parlano italiano.</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-[#1D4ED8]/25 -skew-x-6 z-0"></span>
                </span>
              </h1>

              <p className="mt-7 text-lg lg:text-xl text-[#4A4A4A] max-w-xl leading-relaxed reveal reveal-delay-1">
                Trasformiamo i PDF di <strong className="text-[#0A0A0A]">Google Ads, Meta, AWS, Hetzner e Zoom</strong> in{" "}
                <strong className="text-[#0A0A0A]">autofatture XML FatturaPA</strong> con codice TD17 pre-compilato
                e reverse charge al 22% calcolato dall&apos;AI. In 12 secondi.
                Poi le trasmettiamo allo SDI dell&apos;Agenzia delle Entrate al posto tuo.
              </p>

              <div className="mt-10 flex flex-wrap items-center gap-3">
                <Link
                  to="/register"
                  data-testid="hero-cta-primary"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#0A0A0A] text-white font-semibold text-base hover:bg-[#1D4ED8] shadow-xl shadow-black/15 transition-all"
                >
                  Provala, ci metti un minuto
                  <span className="w-6 h-6 rounded-full bg-white/15 grid place-items-center group-hover:translate-x-1 transition-transform">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
                <a
                  href="#steps"
                  data-testid="hero-cta-secondary"
                  className="inline-flex items-center gap-2 px-6 py-4 rounded-full border border-[#0A0A0A]/15 bg-white/40 backdrop-blur-md text-[#0A0A0A] font-semibold hover:bg-white transition-colors"
                >
                  <MousePointerClick className="w-4 h-4" />
                  Vediamo com&apos;è fatta
                </a>
              </div>

              <div className="mt-12 grid grid-cols-3 gap-6 max-w-xl">
                <HeroStat value="13k+" label="autofatture emesse" />
                <HeroStat value="12s" label="tempo medio elaborazione" />
                <HeroStat value="4,7" label="rating utenti reali" star />
              </div>
            </div>

            {/* Right: layered preview */}
            <div className="lg:col-span-5 relative">
              <HeroPreview />
            </div>
          </div>
        </div>
      </section>

      {/* LIVE TICKER */}
      <section className="py-4 bg-[#0A0A0A] text-white overflow-hidden">
        <div className="marquee-mask overflow-hidden">
          <div className="flex gap-14 whitespace-nowrap marquee-track">
            {[...TICKER, ...TICKER].map((t, i) => (
              <div key={`${t}-${i}`} className="flex items-center gap-3 text-xs md:text-sm font-medium">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                <span className="text-white/60 uppercase tracking-wider text-[10px]">Live</span>
                <span className="text-white/90">{t}</span>
                <span className="text-white/20">·</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="py-16 bg-[#FAF9F6] border-b border-[#E8E4D9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="text-center text-[10px] uppercase tracking-[0.3em] text-[#737373] font-semibold mb-10 reveal">
            Autofatture estere già emesse per fornitori come
          </p>
          <div className="marquee-mask overflow-hidden" data-testid="landing-trust-bar">
            <div
              className="flex items-center gap-12 md:gap-16 whitespace-nowrap marquee-track"
              style={{ animationDuration: "50s" }}
            >
              {[...TRUST, ...TRUST].map((b, i) => (
                <img
                  key={`${b.slug}-${i}`}
                  src={`https://cdn.jsdelivr.net/npm/simple-icons@11/icons/${b.slug}.svg`}
                  alt={b.name}
                  loading="lazy"
                  title={b.name}
                  className="h-6 md:h-7 w-auto opacity-40 hover:opacity-90 transition-opacity duration-300"
                  style={{ filter: "grayscale(100%) brightness(0)" }}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROBLEMS */}
      <section className="py-24 md:py-32 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="reveal">
            <SectionHead
              centered
              kicker="Diciamocelo · Autofattura estera in Italia"
              title={
                <>
                  Fare le fatture di Google Ads a mano<br />
                  <span className="font-serif-accent text-[#F97316]">non è più</span> il 2015.
                </>
              }
              subtitle="Ogni servizio digitale che paghi in UE o extra-UE ti costringe a emettere autofattura verso lo SDI. Se hai 40 PDF di Meta al mese, farlo a mano è una tortura che nessuno paga."
            />
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {PROBLEMS.map((p, idx) => (
              <div
                key={p.title}
                className={`group relative rounded-2xl bg-white border border-[#E8E4D9] p-8 card-lift reveal reveal-delay-${idx + 1}`}
              >
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#737373] font-semibold mb-5">
                  {p.kicker}
                </div>
                <div
                  className={`w-12 h-12 rounded-xl grid place-items-center mb-5 ${
                    p.tone === "coral"
                      ? "bg-[#F97316]/10 text-[#F97316]"
                      : p.tone === "blue"
                      ? "bg-[#1D4ED8]/10 text-[#1D4ED8]"
                      : "bg-[#0A0A0A]/5 text-[#0A0A0A]"
                  }`}
                >
                  <p.icon className="w-5 h-5" />
                </div>
                <h3 className="font-display font-bold text-2xl mb-3 tracking-tight text-[#0A0A0A]">{p.title}</h3>
                <p className="text-[#4A4A4A] leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="features" className="py-24 md:py-32 bg-[#F5F2EA]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="reveal">
            <SectionHead
              centered
              kicker="Come funziona · TD17 · TD18 · TD19"
              title={
                <>
                  Un motore che si occupa<br />
                  <span className="font-serif-accent text-[#1D4ED8]">lui</span> di tutto.
                </>
              }
              subtitle="AI proprietaria addestrata sui top-40 fornitori esteri (Google Ireland, Meta Platforms, Amazon EU, AWS EMEA, Microsoft, Zoom, Hetzner, Canva…). Legge il PDF, sceglie il codice TD corretto, applica il reverse charge 22% e genera l'XML FatturaPA 1.2.2 pronto per lo SDI."
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-14">
            {FEATURES.map((f, i) => (
              <FeatureCard key={f.title} {...f} testid={`feature-${i}`} idx={i} />
            ))}
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section id="steps" className="py-24 md:py-32 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="reveal">
            <SectionHead
              centered
              kicker="Il flusso · PDF → XML → SDI"
              title={
                <>
                  Dal PDF allo SDI,<br />
                  <span className="font-serif-accent text-[#059669]">in tre respiri</span>.
                </>
              }
              subtitle="Trascini il PDF, l'AI compila l'autofattura, tu confermi l'invio all'Agenzia delle Entrate. Nessun campo da riempire a mano, nessun tracciato XML da studiare."
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-16">
            {STEPS.map((s, i) => (
              <div key={s.n} data-testid={`step-${i}`} className={`relative reveal reveal-delay-${i + 1}`}>
                <div className="flex items-baseline gap-3 mb-6">
                  <div className={`font-display font-black text-6xl md:text-7xl tracking-tighter leading-none ${
                    s.accent === "blue" ? "text-[#1D4ED8]" :
                    s.accent === "emerald" ? "text-[#059669]" : "text-[#F97316]"
                  }`}>
                    {s.n}
                  </div>
                  <div className="h-px bg-[#E8E4D9] flex-1 mb-2" />
                </div>
                <h3 className="font-display font-bold text-2xl mb-3 tracking-tight text-[#0A0A0A]">{s.title}</h3>
                <p className="text-[#4A4A4A] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FIC INTEGRATION HIGHLIGHT */}
      <section className="py-20 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-3xl bg-gradient-to-br from-[#0A0A0A] via-[#0F172A] to-[#1E3A8A] text-white p-8 md:p-14 overflow-hidden reveal">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-[#1D4ED8] rounded-full blur-3xl opacity-30" />
            <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-[#F97316] rounded-full blur-3xl opacity-15" />

            <div className="relative flex flex-col items-center text-center gap-10">
              <div className="max-w-2xl">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur border border-white/20 text-xs font-semibold mb-5">
                  <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  Integrazione Fatture in Cloud certificata
                </div>
                <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95]">
                  Autofatture estere<br />
                  <span className="font-serif-accent text-blue-300">dritte in Fatture in Cloud</span>.
                </h2>
                <p className="mt-5 text-white/70 max-w-xl mx-auto leading-relaxed">
                  Il tuo commercialista lavora con Fatture in Cloud (spoiler: 8 su 10 lo fanno). Colleghi
                  l&apos;account con un OAuth, e ogni autofattura estera che generi arriva nella sua company FIC come
                  <em> self supplier invoice</em> — reverse charge già applicato, pronta per l&apos;invio SDI.
                </p>
                <div className="mt-8 flex flex-wrap justify-center gap-3">
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white text-[#0A0A0A] text-sm font-semibold hover:bg-blue-200 transition-colors"
                  >
                    Collega adesso <ArrowUpRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
              <div className="w-full max-w-xl">
                <div className="glass-card rounded-2xl p-5 space-y-3 text-left">
                  <div className="flex items-center gap-2 mb-3">
                    <Cloud className="w-4 h-4 text-blue-500" />
                    <span className="text-xs font-semibold text-[#0A0A0A]/80 uppercase tracking-wider">
                      Live SDI Status
                    </span>
                  </div>
                  <StatusRow label="AF/0142 · Google Ireland" status="Consegnata" tone="emerald" />
                  <StatusRow label="AF/0143 · Meta Ireland" status="Inviata" tone="blue" />
                  <StatusRow label="AF/0144 · AWS EMEA" status="In elaborazione" tone="slate" />
                  <StatusRow label="AF/0145 · Canva Pty" status="Consegnata" tone="emerald" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-32 bg-[#F5F2EA]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="reveal">
            <SectionHead
              centered
              kicker="Recensioni · Freelance e PMI italiane"
              title={
                <>
                  Freelance e PMI che ora<br />
                  <span className="font-serif-accent text-[#1D4ED8]">dormono meglio</span>.
                </>
              }
              subtitle="Centinaia di partite IVA italiane hanno smesso di rincorrere PDF di Google Ads a fine mese. Il tempo salvato lo hanno reinvestito nel lavoro vero."
            />
          </div>
          <div className="grid md:grid-cols-3 gap-5 mt-14">
            {TESTIMONIALS.map((t, i) => (
              <div
                key={t.name}
                data-testid={`testimonial-${i}`}
                className={`rounded-2xl bg-white border border-[#E8E4D9] p-7 flex flex-col card-lift reveal reveal-delay-${i + 1}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[0,1,2,3,4].map(s => <Star key={s} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-[#0A0A0A] leading-relaxed text-lg font-medium flex-1 tracking-tight">
                  &ldquo;{t.quote}&rdquo;
                </p>
                <div className="mt-5 pt-5 border-t border-[#E8E4D9] flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full grid place-items-center font-display font-extrabold text-white text-sm ${
                    t.accent === "blue" ? "bg-[#1D4ED8]" :
                    t.accent === "emerald" ? "bg-[#059669]" : "bg-[#F97316]"
                  }`}>
                    {t.initial}
                  </div>
                  <div>
                    <div className="font-semibold text-[#0A0A0A] text-sm">{t.name}</div>
                    <div className="text-xs text-[#737373]">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SEZIONE ANCORA COMMERCIALISTI — anteprima + CTA verso /commercialisti */}
      <section id="commercialisti" className="py-24 border-t border-[#E8E4D9] bg-white" data-testid="section-commercialisti-teaser">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-center">
            <div className="lg:col-span-2 reveal">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#F5F2EA] text-[#0A0A0A] text-xs font-mono uppercase tracking-wider mb-6">
                <Sparkles className="w-3 h-3 text-[#B8985C]" /> Software per commercialisti
              </div>
              <h2 className="font-display font-black text-4xl md:text-5xl leading-[1.05] tracking-tight mb-5">
                Uno <em className="italic font-serif text-[#B8985C]">studio commercialista</em>?<br />
                Ti abbiamo pensato a parte.
              </h2>
              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-8">
                Un solo wallet crediti per gestire tutte le aziende clienti che devono emettere autofatture estere. Dashboard consolidata multi-cliente con semaforo di salute, indirizzo email dedicato per ciascun cliente, integrazione Fatture in Cloud per la trasmissione allo SDI dal gestionale che il tuo studio già usa.
              </p>
              <ul className="space-y-2.5 text-sm text-[#4A4A4A] mb-8">
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> Aziende clienti illimitate su qualsiasi piano</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> Un pagamento consolidato, N clienti gestiti</li>
                <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" /> Priority support SLA 4h lavorative sul piano Studio</li>
              </ul>
              <Link
                to="/commercialisti"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#0A0A0A] text-white font-bold hover:bg-black transition-colors shadow-lg"
                data-testid="commercialisti-teaser-cta"
              >
                Scopri il prodotto studio <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="lg:col-span-3 reveal reveal-delay-1">
              <div className="rounded-2xl overflow-hidden border border-[#E8E4D9] shadow-2xl">
                <img
                  src="/screens/commercialista-dashboard.png"
                  alt="Dashboard commercialista Autofattura con 6 clienti gestiti"
                  className="w-full h-auto"
                  loading="lazy"
                  data-testid="commercialisti-teaser-screenshot"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-24 md:py-32 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <SectionHead
            kicker="Prezzi"
            title={
              <>
                Paghi solo <span className="font-serif-accent text-[#059669]">quando serve</span>.
              </>
            }
            subtitle="5 crediti omaggio inclusi al signup. Poi pagamento unico, senza abbonamento, senza scadenza. Ideale per chi ha volumi variabili."
          />

          <div className="grid md:grid-cols-3 gap-6 md:gap-4 items-stretch max-w-5xl mx-auto mt-14">
            {PLANS.map((p) => (
              <div
                key={p.id}
                data-testid={`pricing-card-${p.id}`}
                className={`relative rounded-3xl p-8 flex flex-col transition-all ${
                  p.highlight
                    ? "bg-[#0A0A0A] text-white border-2 border-[#1D4ED8] shadow-2xl shadow-[#1D4ED8]/30 md:-translate-y-4 md:scale-[1.04]"
                    : "bg-white text-[#0A0A0A] border border-[#E8E4D9] hover:border-[#0A0A0A]/30 hover:shadow-xl"
                }`}
              >
                {p.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-gradient-to-r from-[#1D4ED8] to-[#F97316] text-white text-[10px] font-bold tracking-widest uppercase shadow-lg">
                    {p.badge}
                  </div>
                )}
                <div className={`text-[10px] uppercase tracking-[0.3em] font-bold mb-4 ${p.highlight ? "text-blue-400" : "text-[#737373]"}`}>
                  {p.name}
                </div>
                <div className="flex items-baseline gap-2 mb-1">
                  <span className={`font-display font-extrabold text-5xl tracking-tighter ${p.highlight ? "text-white" : "text-[#0A0A0A]"}`}>
                    {p.price}
                  </span>
                  <span className={p.highlight ? "text-white/50 text-sm" : "text-[#737373] text-sm"}>{p.unit}</span>
                </div>
                <p className={`text-sm mb-6 ${p.highlight ? "text-white/60" : "text-[#4A4A4A]"}`}>{p.desc}</p>
                <ul className="space-y-3 mb-8 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm">
                      <CheckCircle2 className={`w-4 h-4 shrink-0 mt-0.5 ${p.highlight ? "text-emerald-400" : "text-[#059669]"}`} />
                      <span className={p.highlight ? "text-white/90" : "text-[#0A0A0A]"}>{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={user ? `/app/account/credits?plan=${p.id}` : `/register?plan=${p.id}`}
                  data-testid={`pricing-cta-${p.id}`}
                  className={`group w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-sm font-semibold transition-all ${
                    p.highlight
                      ? "bg-white text-[#0A0A0A] hover:bg-blue-100"
                      : "bg-[#0A0A0A] text-white hover:bg-[#1D4ED8]"
                  }`}
                >
                  {p.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 bg-[#F5F2EA]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="reveal">
            <SectionHead
              centered
              kicker="FAQ · Autofattura estera in Italia"
              title={
                <>
                  Le domande che ci fate <span className="font-serif-accent text-[#1D4ED8]">spesso</span>.
                </>
              }
              subtitle="Reverse charge, codici TD17/TD18, regime forfettario, invio SDI: tutto quello che serve capire prima di iscriversi."
            />
          </div>

          <div className="mt-14 space-y-3">
            {FAQ.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div
                  key={f.q}
                  data-testid={`faq-item-${i}`}
                  className={`rounded-2xl bg-white border transition-all overflow-hidden reveal ${
                    isOpen ? "border-[#1D4ED8] shadow-lg shadow-[#1D4ED8]/10" : "border-[#E8E4D9] hover:border-[#0A0A0A]/30"
                  }`}
                >
                  <button
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                    className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left group"
                  >
                    <span className="font-display font-bold text-lg text-[#0A0A0A] pr-4">
                      {f.q}
                    </span>
                    <span className={`w-8 h-8 shrink-0 rounded-full grid place-items-center transition-all ${
                      isOpen ? "bg-[#1D4ED8] text-white rotate-90" : "border border-[#E8E4D9] text-[#737373] group-hover:border-[#0A0A0A] group-hover:text-[#0A0A0A]"
                    }`}>
                      {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                    </span>
                  </button>
                  <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96" : "max-h-0"}`}>
                    <p className="px-6 pb-6 text-[#4A4A4A] leading-relaxed">{f.a}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <p className="mt-10 text-center text-sm text-[#737373]">
            Hai ancora domande?{" "}
            <a href="mailto:team@dasabo.com" className="text-[#1D4ED8] font-semibold hover:underline">
              Scrivi al team
            </a>
          </p>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 md:py-32 bg-[#FAF9F6]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="relative rounded-[2.5rem] bg-gradient-to-br from-[#0A0A0A] via-[#0F172A] to-[#1E3A8A] text-white p-10 md:p-16 overflow-hidden text-center reveal">
            <div className="absolute -top-32 -right-32 w-[500px] h-[500px] bg-[#1D4ED8] rounded-full blur-3xl opacity-40" />
            <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] bg-[#F97316] rounded-full blur-3xl opacity-25" />
            <div className="relative">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 backdrop-blur text-xs font-semibold mb-6">
                <Timer className="w-3.5 h-3.5 text-emerald-400" />
                3 minuti per il primo XML FatturaPA
              </div>
              <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-7xl tracking-tighter leading-[0.95]">
                La prossima fattura di Google Ads<br />
                la <span className="font-serif-accent text-blue-300">automatizzi stasera</span>.
              </h2>
              <p className="mt-6 text-lg md:text-xl text-white/60 max-w-xl mx-auto">
                5 crediti omaggio al signup. Nessuna carta di credito richiesta. Se non ti convince, chiudi la scheda e amici come prima.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
                <Link
                  to="/register"
                  data-testid="final-cta-primary"
                  className="group inline-flex items-center gap-2 px-7 py-4 rounded-full bg-white text-[#0A0A0A] font-semibold hover:bg-blue-100 transition-colors shadow-2xl shadow-black/40"
                >
                  Prova gratis
                  <span className="w-6 h-6 rounded-full bg-[#0A0A0A] text-white grid place-items-center group-hover:translate-x-1 transition-transform">
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 px-7 py-4 rounded-full border border-white/20 text-white font-semibold hover:bg-white/10 transition-colors"
                >
                  Ho già un account
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0A0A0A] text-[#737373] py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-5 gap-10">
          <div className="md:col-span-2">
            <div className="flex items-center gap-2 text-white font-display font-extrabold text-xl">
              Autofattura by
              <img src={DASABO_LOGO} alt="Dasabo" className="h-6 w-auto brightness-0 invert" />
            </div>
            <p className="mt-4 text-sm max-w-sm leading-relaxed">
              Autofattura estera automatizzata per freelance, PMI e commercialisti italiani.
              XML FatturaPA con codici TD17, TD18 e TD19 conformi al tracciato SDI 1.2.2, generati in 30 secondi con AI proprietaria.
              Fatto a Palermo da persone che odiavano compilare fatture a mano.
            </p>
            <div className="mt-6 flex items-center gap-1 text-amber-400">
              {[0,1,2,3,4].map(i => <Star key={i} className="w-3 h-3 fill-current" />)}
              <span className="ml-2 text-sm text-[#737373]">4,7 su 500+ freelance</span>
            </div>
          </div>
          <div>
            <div className="text-white font-semibold text-sm mb-4">Prodotto</div>
            <ul className="space-y-2.5 text-sm">
              <li><a href="#features" className="hover:text-white transition-colors">Funzionalità</a></li>
              <li><a href="#pricing" className="hover:text-white transition-colors">Prezzi</a></li>
              <li><Link to="/commercialisti" className="hover:text-white transition-colors" data-testid="footer-commercialisti-link">Per commercialisti</Link></li>
              <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              <li><Link to="/login" className="hover:text-white transition-colors">Accedi</Link></li>
              <li><Link to="/register" className="hover:text-white transition-colors">Prova gratis</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold text-sm mb-4">Autofatture per</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/autofattura/google-ads" className="hover:text-white transition-colors" data-testid="footer-supplier-google">Google Ads</Link></li>
              <li><Link to="/autofattura/meta-ads" className="hover:text-white transition-colors" data-testid="footer-supplier-meta">Meta Ads (Facebook/Instagram)</Link></li>
              <li><Link to="/autofattura/amazon-aws" className="hover:text-white transition-colors" data-testid="footer-supplier-aws">Amazon AWS</Link></li>
              <li><Link to="/autofattura/zoom" className="hover:text-white transition-colors" data-testid="footer-supplier-zoom">Zoom</Link></li>
              <li><Link to="/autofattura/hetzner" className="hover:text-white transition-colors" data-testid="footer-supplier-hetzner">Hetzner</Link></li>
              <li><Link to="/blog" className="hover:text-white transition-colors" data-testid="footer-blog-link">Blog · Guide fiscali</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-white font-semibold text-sm mb-4">Legal</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white transition-colors">Termini di servizio</Link></li>
              <li><Link to="/cookies" className="hover:text-white transition-colors">Cookie policy</Link></li>
              <li><Link to="/gdpr" className="hover:text-white transition-colors">GDPR</Link></li>
              <li><Link to="/docs" className="hover:text-white transition-colors" data-testid="footer-docs-link">Documentazione</Link></li>
              <li>
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new CustomEvent("cookie:open"))}
                  className="hover:text-white transition-colors text-left"
                  data-testid="footer-cookie-preferences"
                >
                  Preferenze cookie
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 mt-12 pt-8 border-t border-white/10 flex items-center justify-between flex-wrap gap-4 text-xs">
          <div>
            © 2026 <span className="text-white/90 font-semibold">DASABO SRL</span> ·
            P.IVA/CF IT07407380828 ·
            Via Imperatore Federico 100, 90143 Palermo (PA), Italia
          </div>
          <div className="flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            <span>Servizio operativo</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/* -------- helpers -------- */
function HeroStat({ value, label, star }) {
  return (
    <div>
      <div className="font-display font-extrabold text-3xl md:text-4xl tracking-tighter text-[#0A0A0A] flex items-center gap-1.5">
        {value}
        {star && <Star className="w-5 h-5 fill-amber-400 text-amber-400 mt-1" />}
      </div>
      <div className="text-xs text-[#737373] mt-1 leading-tight">{label}</div>
    </div>
  );
}

function SectionHead({ kicker, title, subtitle, centered }) {
  return (
    <div className={`max-w-3xl ${centered ? "mx-auto text-center" : ""}`}>
      <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-4">
        <span className="w-6 h-px bg-[#1D4ED8]" />
        {kicker}
      </div>
      <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[0.95] text-[#0A0A0A]">
        {title}
      </h2>
      {subtitle && (
        <p className="mt-5 text-lg text-[#4A4A4A] leading-relaxed">{subtitle}</p>
      )}
    </div>
  );
}

function FeatureCard({ icon: Icon, title, body, color, testid, idx = 0 }) {
  const bg = {
    blue: "bg-[#1D4ED8]/8 text-[#1D4ED8]",
    emerald: "bg-[#059669]/10 text-[#059669]",
    coral: "bg-[#F97316]/10 text-[#F97316]",
    ink: "bg-[#0A0A0A]/5 text-[#0A0A0A]",
  }[color];
  return (
    <div
      data-testid={testid}
      className={`group relative rounded-3xl bg-white border border-[#E8E4D9] p-8 md:p-10 card-lift hover:border-[#0A0A0A]/40 overflow-hidden reveal reveal-delay-${(idx % 4) + 1}`}
    >
      <div className={`w-14 h-14 rounded-2xl grid place-items-center mb-6 ${bg} group-hover:scale-110 transition-transform`}>
        <Icon className="w-6 h-6" />
      </div>
      <h3 className="font-display font-bold text-2xl mb-3 tracking-tight text-[#0A0A0A]">{title}</h3>
      <p className="text-[#4A4A4A] leading-relaxed">{body}</p>
    </div>
  );
}

function StatusRow({ label, status, tone }) {
  const tones = {
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    slate: "bg-slate-50 text-slate-700 border-slate-200",
  };
  return (
    <div className="flex items-center justify-between bg-white/50 rounded-xl px-4 py-2.5 border border-white/40">
      <span className="font-mono text-xs text-[#0A0A0A]/70 truncate">{label}</span>
      <span className={`text-[10px] px-2.5 py-0.5 rounded-full border font-semibold whitespace-nowrap ${tones[tone]}`}>
        {status}
      </span>
    </div>
  );
}

function HeroPreview() {
  return (
    <div className="relative">
      {/* Background gradient blob */}
      <div className="absolute -inset-8 bg-gradient-to-br from-[#1D4ED8]/20 via-[#F97316]/10 to-[#059669]/15 rounded-[3rem] blur-2xl" />

      {/* Main card */}
      <div className="relative rounded-3xl bg-white border border-[#E8E4D9] shadow-2xl shadow-[#0A0A0A]/10 p-6 md:p-7 z-10">
        <div className="flex items-center justify-between mb-4 pb-4 border-b border-[#E8E4D9]">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 via-yellow-500 to-blue-500 grid place-items-center text-white font-display font-black">G</div>
            <div>
              <div className="font-semibold text-[#0A0A0A] leading-tight">Google Ireland Ltd.</div>
              <div className="text-xs text-[#737373] font-mono">IE6388047V</div>
            </div>
          </div>
          <span className="text-[10px] px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase tracking-wider">
            Estratto in 12s
          </span>
        </div>
        <div className="space-y-2.5 text-sm">
          <RowKV k="Nr. fattura" v="5289301923" mono />
          <RowKV k="Data" v="31/01/2026" mono />
          <RowKV k="Imponibile" v="€ 1.243,56" mono bold />
          <RowKV k="IVA (Rev.Charge)" v="€ 273,58" mono />
          <div className="flex justify-between py-2 border-t border-[#E8E4D9] mt-1">
            <span className="text-[#737373]">Tipo doc.</span>
            <span className="font-mono font-bold text-[#1D4ED8]">TD17 · Servizi UE</span>
          </div>
        </div>
        <button
          type="button"
          disabled
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-[#0A0A0A] text-white text-sm font-semibold cursor-default"
        >
          <Zap className="w-4 h-4" /> Invia allo SDI
        </button>
      </div>

      {/* Floating card 1 - top right */}
      <div className="absolute -top-6 -right-4 md:-right-8 rounded-2xl bg-white border border-[#E8E4D9] shadow-xl p-4 z-20 max-w-[200px] rotate-3 float-slow" style={{ "--rot": "3deg" }}>
        <div className="flex items-center gap-2 mb-1.5">
          <Timer className="w-4 h-4 text-[#059669]" />
          <span className="text-[10px] uppercase tracking-wider text-[#737373] font-semibold">Tempo salvato</span>
        </div>
        <div className="font-display font-extrabold text-2xl tracking-tighter text-[#0A0A0A]">12h / mese</div>
      </div>

      {/* Floating card 2 - bottom left */}
      <div className="absolute -bottom-8 -left-4 md:-left-6 rounded-2xl bg-[#0A0A0A] text-white border border-[#0A0A0A] shadow-xl p-4 z-20 max-w-[220px] -rotate-2 float-slow-delay" style={{ "--rot": "-2deg" }}>
        <div className="flex items-center gap-2 mb-1.5">
          <Receipt className="w-4 h-4 text-blue-400" />
          <span className="text-[10px] uppercase tracking-wider text-white/60 font-semibold">Questo mese</span>
        </div>
        <div className="font-display font-extrabold text-2xl tracking-tighter">47 autofatture</div>
        <div className="text-xs text-white/60 mt-0.5">tutte consegnate ✓</div>
      </div>
    </div>
  );
}

function RowKV({ k, v, mono, bold }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-[#737373] text-sm">{k}</span>
      <span className={`${mono ? "font-mono" : ""} ${bold ? "font-bold text-[#0A0A0A]" : "text-[#0A0A0A]"} text-sm`}>{v}</span>
    </div>
  );
}
